import React, { useState } from 'react';
import {
    Box,
    Container,
    Typography,
    Grid,
    Stack,
    Divider,
    Link,
    useTheme,
} from '@mui/material';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import Header from '../components/Header';
import { useNavigate } from 'react-router-dom';
import Footer from '../components/Footer';

// Define the component props interface
interface ContactUsPageProps {
    mode: 'light' | 'dark';
    setMode: (mode: 'light' | 'dark') => void;
}

const ContactUsPage = ({ mode, setMode }: ContactUsPageProps) => {
    const navigate = useNavigate();
    const theme = useTheme();
    const [isLoggedIn, setLoggedin] = useState<boolean>(false);

    // Contact Information
    const contactInfo = {
        email: 'hello@workhorse.com.sg',
        phone: '+65 9837 8090',
        whatsapp: '+65 9837 8090',
        address:
            'A-6-13, The Tube, Jalan PJU 1/39, Dataran Prima, 47301 Petaling Jaya, Selangor',
        linkedin: 'https://www.linkedin.com/company/workhorse-sg/',
    };

    // Attractive Contact Item Wrapper
    const AttractiveContactItem = ({
        icon,
        title,
        content,
        link,
        linkType = 'text',
        iconColor,
        hoverBgColor,
    }: {
        icon: React.ReactNode;
        title: string;
        content: string;
        link?: string;
        linkType?: 'tel' | 'mailto' | 'text';
        iconColor: string;
        hoverBgColor: string;
    }) => {
        let finalLink = link;

        if (link && linkType !== 'text') {
            finalLink = `${linkType}:${link}`;
        } else if (linkType === 'text' && title === 'WhatsApp') {
            finalLink = `https://wa.me/${contactInfo.whatsapp.replace(
                /[^0-9]/g,
                '',
            )}`;
        } else if (linkType === 'text' && title === 'Our Location') {
            finalLink = `https://www.google.com/maps?q=${encodeURIComponent(
                content,
            )}`;
        }

        // Wrapper ensures no component={Link} is used inside <Box> (prevents recursion crash)
        const Wrapper = ({ children }: { children: React.ReactNode }) =>
            finalLink ? (
                <Link
                    href={finalLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="none"
                    sx={{ display: 'block' }}
                >
                    {children}
                </Link>
            ) : (
                <>{children}</>
            );

        return (
            <Wrapper>
                <Box
                    sx={{
                        p: 3,
                        height: '100%',
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 2,
                        boxShadow: theme.shadows[1],
                        transition: '0.3s',
                        '&:hover': {
                            boxShadow: theme.shadows[6],
                            backgroundColor: hoverBgColor,
                        },
                        cursor: finalLink ? 'pointer' : 'default',
                    }}
                >
                    <Stack direction="row" spacing={2} alignItems="flex-start">
                        <Box sx={{ color: iconColor, pt: 0.5 }}>
                            <Box sx={{ fontSize: 40 }}>
                                {icon}
                            </Box>
                        </Box>
                        <Box>
                            <Typography variant="h6" fontWeight="bold">
                                {title}
                            </Typography>
                            <Typography variant="body1" color="text.secondary">
                                {content}
                            </Typography>
                        </Box>
                    </Stack>
                </Box>
            </Wrapper>
        );
    };

    // Correct Google Maps embed URL
    const mapEmbedUrl = `https://www.google.com/maps?q=${encodeURIComponent(
        contactInfo.address,
    )}&output=embed`;

    return (
        <>
            <Header isLoggedIn={isLoggedIn} mode={mode} setMode={setMode} />
            <Box>
                {/* HERO */}
                <Box
                    sx={{
                        backgroundColor:
                            theme.palette.mode === 'light'
                                ? '#E3F2FD'
                                : theme.palette.background.paper,
                        py: { xs: 10, sm: 14 },
                        textAlign: 'center',
                    }}
                >
                    <Container maxWidth="md">
                        <Typography
                            variant="h2"
                            fontWeight={800}
                            gutterBottom
                            sx={{ color: "text.primary" }}
                        >
                            Let's Talk Business
                        </Typography>
                        <Typography variant="h5" color="text.secondary" mb={4}>
                            We're ready to partner with you. Reach out to the Work Horse
                            team today.
                        </Typography>
                    </Container>
                </Box>

                <Container maxWidth="lg" sx={{ py: 8 }}>
                    <Typography
                        variant="h3"
                        fontWeight="bold"
                        gutterBottom
                        textAlign="center"
                        mb={6}
                    >
                        Connect With Us
                    </Typography>

                    <Grid container spacing={4}>
                        {/* EMAIL */}
                        <Grid size={{ xs: 12, sm: 6, md: 3 }} >
                            <AttractiveContactItem
                                icon={<EmailIcon />}
                                title="Email Us"
                                content={contactInfo.email}
                                link={contactInfo.email}
                                linkType="mailto"
                                iconColor={theme.palette.secondary.main}
                                hoverBgColor={
                                    theme.palette.mode === 'light'
                                        ? 'rgba(255, 206, 0, 0.1)'
                                        : 'rgba(255, 206, 0, 0.05)'
                                }
                            />
                        </Grid>

                        {/* PHONE */}
                        <Grid size={{ xs: 12, sm: 6, md: 3 }} >
                            <AttractiveContactItem
                                icon={<PhoneIcon />}
                                title="Call Us"
                                content={contactInfo.phone}
                                link={contactInfo.phone}
                                linkType="tel"
                                iconColor={theme.palette.primary.dark}
                                hoverBgColor={
                                    theme.palette.mode === 'light'
                                        ? 'rgba(227, 242, 253, 0.5)'
                                        : 'rgba(227, 242, 253, 0.05)'
                                }
                            />
                        </Grid>

                        {/* WHATSAPP */}
                        <Grid size={{ xs: 12, sm: 6, md: 3 }} >
                            <AttractiveContactItem
                                icon={<WhatsAppIcon />}
                                title="WhatsApp"
                                content={contactInfo.whatsapp}
                                link={contactInfo.whatsapp}
                                linkType="text"
                                iconColor="#25D366"
                                hoverBgColor={
                                    theme.palette.mode === 'light'
                                        ? 'rgba(37, 211, 102, 0.1)'
                                        : 'rgba(37, 211, 102, 0.05)'
                                }
                            />
                        </Grid>

                        {/* LINKEDIN */}
                        <Grid size={{ xs: 12, sm: 6, md: 3 }} >
                            <AttractiveContactItem
                                icon={<LinkedInIcon />}
                                title="LinkedIn"
                                content="Work Horse SG"
                                link={contactInfo.linkedin}
                                linkType="text"
                                iconColor="#0077B5"
                                hoverBgColor={
                                    theme.palette.mode === 'light'
                                        ? 'rgba(0, 119, 181, 0.1)'
                                        : 'rgba(0, 119, 181, 0.05)'
                                }
                            />
                        </Grid>

                        <Grid size={{ xs: 12 }} >
                            <Divider sx={{ my: 4 }} />
                        </Grid>

                        {/* MAP & Location */}
                        <Grid size={{ xs: 12 }} >
                            <Stack
                                direction="row"
                                spacing={2}
                                alignItems="center"
                                mb={3}
                            >
                                <LocationOnIcon
                                    sx={{
                                        fontSize: 30,
                                        color: theme.palette.primary.main,
                                    }}
                                />
                                <Typography variant="h4" fontWeight="bold">
                                    Visit Us
                                </Typography>
                            </Stack>

                            <AttractiveContactItem
                                icon={<LocationOnIcon />}
                                title="Our Location"
                                content={contactInfo.address}
                                link={contactInfo.address}
                                linkType="text"
                                iconColor={theme.palette.error.main}
                                hoverBgColor={
                                    theme.palette.mode === 'light'
                                        ? 'rgba(255, 0, 0, 0.05)'
                                        : 'rgba(255, 0, 0, 0.02)'
                                }
                            />

                            <Box
                                sx={{
                                    height: { xs: 300, sm: 450 },
                                    width: '100%',
                                    borderRadius: 3,
                                    overflow: 'hidden',
                                    boxShadow: theme.shadows[10],
                                    mt: 3,
                                }}
                            >
                                <iframe
                                    title="WorkHorse Location Map"
                                    width="100%"
                                    height="100%"
                                    loading="lazy"
                                    allowFullScreen
                                    src={mapEmbedUrl}
                                    style={{ border: 0 }}
                                />
                            </Box>
                        </Grid>
                    </Grid>
                </Container>
            </Box>
            <Footer />
        </>
    );
};

export default ContactUsPage;
